import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tabButton", "tabPanel", "vinInput", "vinResult", "vinPhoto", "vinPhotoInput",
                     "storedList", "newCoachFields", "coachIdField",
                     "newYear", "newMake", "newModel", "newVin", "selectedCoachInfo"]

  connect() {
    // Default to VIN Lookup for intake, Stored for others
    const inspType = document.querySelector('input[name="facility_inspection[inspection_type]"]:checked')
    const defaultTab = (inspType && inspType.value === "intake") ? "vin" : "stored"
    this.switchTab(defaultTab)
  }

  selectTab(e) {
    const tab = e.currentTarget.dataset.tab
    this.switchTab(tab)
  }

  switchTab(tab) {
    this.tabButtonTargets.forEach(btn => {
      const active = btn.dataset.tab === tab
      btn.classList.toggle("border-blue-500", active)
      btn.classList.toggle("text-blue-400", active)
      btn.classList.toggle("border-transparent", !active)
      btn.classList.toggle("text-white/40", !active)
    })
    this.tabPanelTargets.forEach(panel => {
      panel.classList.toggle("hidden", panel.dataset.tab !== tab)
    })
    // Clear coach_id when switching to new coach tab
    if (tab === "new") {
      this.coachIdFieldTarget.value = ""
    }
  }

  async searchVin() {
    const vin = this.vinInputTarget.value.trim().toUpperCase()
    if (vin.length < 5) {
      this.vinResultTarget.innerHTML = '<p class="text-amber-400 text-sm"><i class="fas fa-exclamation-triangle mr-1"></i>Enter at least 5 characters</p>'
      return
    }

    this.vinResultTarget.innerHTML = '<p class="text-white/40 text-sm"><i class="fas fa-spinner fa-spin mr-1"></i>Searching...</p>'

    try {
      const resp = await fetch("/facility/inspections/search_coach", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ vin })
      })
      const data = await resp.json()

      if (data.found) {
        this.coachIdFieldTarget.value = data.coach.id
        this.vinResultTarget.innerHTML = `
          <div class="p-3 bg-green-500/10 border border-green-500/20 rounded-xl">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-green-500/15 flex items-center justify-center flex-shrink-0">
                <i class="fas fa-check-circle text-green-400"></i>
              </div>
              <div class="flex-1">
                <p class="text-white font-medium text-sm">${data.coach.display_name}</p>
                <p class="text-xs text-white/40">VIN: ${data.coach.vin} · Owner: ${data.coach.owner || 'Unassigned'}</p>
                ${data.coach.stored ? '<span class="inline-block mt-1 text-[10px] px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded-full">Currently Stored</span>' : ''}
              </div>
            </div>
          </div>`
      } else {
        this.vinResultTarget.innerHTML = `
          <div class="p-3 bg-white/[0.04] border border-white/10 rounded-xl text-center">
            <i class="fas fa-search text-xl text-white/20 mb-2"></i>
            <p class="text-white/40 text-sm">No coach found with VIN "${vin}"</p>
            <button type="button" data-action="inspection-coach#createFromVin" class="mt-2 text-blue-400 hover:text-blue-300 text-sm font-medium transition">
              <i class="fas fa-plus mr-1"></i>Create new coach with this VIN
            </button>
          </div>`
      }
    } catch (err) {
      this.vinResultTarget.innerHTML = '<p class="text-red-400 text-sm"><i class="fas fa-exclamation-circle mr-1"></i>Search failed. Try again.</p>'
    }
  }

  createFromVin() {
    this.switchTab("new")
    this.newVinTarget.value = this.vinInputTarget.value.trim().toUpperCase()
  }

  selectStoredCoach(e) {
    const card = e.currentTarget
    const coachId = card.dataset.coachId
    this.coachIdFieldTarget.value = coachId
    // Visual selection
    document.querySelectorAll('.coach-select-card').forEach(c => c.classList.remove('selected'))
    card.classList.add('selected')
    // Clear new coach fields
    if (this.hasNewYearTarget) this.newYearTarget.value = ""
    if (this.hasNewMakeTarget) this.newMakeTarget.value = ""
    if (this.hasNewModelTarget) this.newModelTarget.value = ""
    if (this.hasNewVinTarget) this.newVinTarget.value = ""
  }

  captureVinPhoto() {
    this.vinPhotoInputTarget.click()
  }

  vinPhotoSelected(e) {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      this.vinPhotoTarget.innerHTML = `
        <div class="relative rounded-xl overflow-hidden border border-white/10 mb-3">
          <img src="${ev.target.result}" class="w-full max-h-48 object-contain bg-black/50" alt="VIN plate photo">
          <button type="button" data-action="inspection-coach#clearVinPhoto" class="absolute top-2 right-2 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center text-white/60 hover:text-white">
            <i class="fas fa-times text-xs"></i>
          </button>
        </div>
        <p class="text-white/30 text-xs text-center mb-2">Type the VIN from the photo above</p>`
    }
    reader.readAsDataURL(file)
  }

  clearVinPhoto() {
    this.vinPhotoTarget.innerHTML = ""
    this.vinPhotoInputTarget.value = ""
  }

  // When inspection type changes, switch default tab
  typeChanged(e) {
    const type = e.target.value
    if (type === "intake") {
      this.switchTab("vin")
    } else {
      this.switchTab("stored")
    }
  }
}
